import React from 'react'
import { Box, Chip, Grid, Paper, Stack, Typography, Button, Breadcrumbs, Link, Alert } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import AdSlot from '../components/AdSlot'
import VideoCard, { Video } from '../components/VideoCard'
import { useTranslation } from 'react-i18next'
import { ArrowBack as ArrowBackIcon, Home as HomeIcon, NavigateNext as NextIcon, NavigateBefore as PrevIcon } from '@mui/icons-material'
import { maybeAttachAuthorization } from '@mahendran/shared-frontend-config'
import { buildApiUrl } from '../lib/apiBase'
import { VideoSuggestion, VideoSeries } from '../types/video'

function extractYoutubeId(url: string): string | null {
    try {
        const u = new URL(url)
        if (u.hostname.includes('youtube.com')) {
            const v = u.searchParams.get('v'); if (v) return v
            const parts = u.pathname.split('/').filter(Boolean)
            const idx = parts.indexOf('embed'); if (idx >= 0 && parts[idx + 1]) return parts[idx + 1]
        }
        if (u.hostname.includes('youtu.be')) { const id = u.pathname.replace('/', ''); if (id) return id }
    } catch { }
    return null
}

const fetchWithAuth = async (url: string, options: RequestInit = {}) => {
    const headers = new Headers(options.headers);
    const credentials = options.credentials || 'include';
    maybeAttachAuthorization(headers, credentials as RequestCredentials);
    const res = await fetch(url, { ...options, headers, credentials });
    return res;
}

const VideoDetail: React.FC = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const { t } = useTranslation()
    const [loading, setLoading] = React.useState(true)
    const [error, setError] = React.useState<string | null>(null)
    const [attempt, setAttempt] = React.useState(0)
    const [item, setItem] = React.useState<VideoSuggestion | null>(null)
    const [related, setRelated] = React.useState<Video[]>([])
    const [series, setSeries] = React.useState<VideoSeries | null>(null)
    const [seriesVideos, setSeriesVideos] = React.useState<VideoSuggestion[]>([])
    const [currentIndex, setCurrentIndex] = React.useState<number>(-1)

    React.useEffect(() => {
        let active = true
        setLoading(true)
        setError(null)
        setItem(null)
        setRelated([])
        setSeries(null)
        setSeriesVideos([])
        setCurrentIndex(-1)
        void (async () => {
            try {
                const res = await fetchWithAuth(buildApiUrl(`/video-suggestions/${encodeURIComponent(id || '')}`))
                if (!res.ok) throw new Error(res.status === 404 ? 'Video nicht gefunden.' : 'Video konnte nicht geladen werden.')
                const data: VideoSuggestion = await res.json()
                if (!active) return
                setItem(data)
                setLoading(false)

                // If video belongs to a series, load series data and videos
                if (data.series?.slug) {
                    try {
                        // Load series info
                        const seriesRes = await fetchWithAuth(buildApiUrl(`/video-series/${encodeURIComponent(data.series.slug)}`))
                        if (seriesRes.ok) {
                            const seriesData = await seriesRes.json()
                            if (active) setSeries(seriesData)
                        }

                        // Load all videos in series
                        const seriesVideosRes = await fetchWithAuth(buildApiUrl(`/video-suggestions/approved/series/${encodeURIComponent(data.series.slug)}`))
                        if (seriesVideosRes.ok) {
                            const seriesVideosData = await seriesVideosRes.json()
                            if (!active) return
                            setSeriesVideos(seriesVideosData)

                            // Find current video index in series
                            const index = seriesVideosData.findIndex((v: VideoSuggestion) => v.id === data.id)
                            setCurrentIndex(index)
                        }
                    } catch (error) {
                        console.error('Failed to load series data:', error)
                    }
                }
            } catch (error) {
                if (active) setError(error instanceof Error ? error.message : 'Video konnte nicht geladen werden.')
            } finally {
                if (active) setLoading(false)
            }
        })()
        void (async () => {
            try {
                const res = await fetchWithAuth(buildApiUrl('/video-suggestions/approved'))
                if (res.ok) {
                    const list: VideoSuggestion[] = await res.json()
                    const vids: Video[] = list
                        .filter(s => String(s.id) !== String(id))
                        .slice(0, 6)
                        .map(s => {
                            const yt = extractYoutubeId(s.youtubeUrl)
                            const thumb = yt ? `https://img.youtube.com/vi/${yt}/hqdefault.jpg` : 'https://picsum.photos/seed/fallback/600/338'
                            const tags = (s.tags || '').split(',').map(t => t.trim()).filter(Boolean)
                            return { id: String(s.id), title: s.title, thumbnailUrl: thumb, duration: '', tags }
                        })
                    if (active) setRelated(vids)
                }
            } catch { }
        })()
        return () => { active = false }
    }, [id, attempt])

    const ytId = item ? extractYoutubeId(item.youtubeUrl) : null
    const itemTags = (item?.tags || '').split(',').map(t => t.trim()).filter(Boolean)

    // Navigation functions
    const goToPreviousVideo = () => {
        if (currentIndex > 0 && seriesVideos[currentIndex - 1]) {
            navigate(`/video/${seriesVideos[currentIndex - 1].id}`)
        }
    }

    const goToNextVideo = () => {
        if (currentIndex < seriesVideos.length - 1 && seriesVideos[currentIndex + 1]) {
            navigate(`/video/${seriesVideos[currentIndex + 1].id}`)
        }
    }

    const goToSeries = () => {
        if (series?.slug) {
            navigate(`/videos?series=${encodeURIComponent(series.slug)}`)
        }
    }

    if (loading) return <Typography role="status">Video wird geladen…</Typography>
    if (error) return <Stack spacing={2}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => setAttempt(value => value + 1)}>Erneut versuchen</Button>
        <Button onClick={() => navigate('/videos')}>Zur Videoübersicht</Button>
    </Stack>

    return (
        <Box sx={{ py: { xs: 3, md: 4 } }}>
            {/* Breadcrumbs */}
            <Breadcrumbs sx={{ mb: 2 }}>
                <Link
                    component="button"
                    variant="body2"
                    onClick={() => navigate('/')}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                    <HomeIcon fontSize="small" />
                    Home
                </Link>
                {series && (
                    <Link
                        component="button"
                        variant="body2"
                        onClick={goToSeries}
                    >
                        {series.title}
                    </Link>
                )}
                <Typography variant="body2" color="text.primary">
                    {item?.title}
                </Typography>
            </Breadcrumbs>

            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => {
                    try {
                        if (window.history.length > 1) return navigate(-1)
                    } catch { }
                    navigate('/videos')
                }}>
                    Zurück
                </Button>

                {/* Series Navigation */}
                {series && currentIndex >= 0 && seriesVideos.length > 1 && (
                    <Stack direction="row" spacing={1}>
                        <Button
                            startIcon={<PrevIcon />}
                            onClick={goToPreviousVideo}
                            disabled={currentIndex <= 0}
                            size="small"
                        >
                            Vorheriges
                        </Button>
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                            {currentIndex + 1} / {seriesVideos.length}
                        </Typography>
                        <Button
                            endIcon={<NextIcon />}
                            onClick={goToNextVideo}
                            disabled={currentIndex >= seriesVideos.length - 1}
                            size="small"
                        >
                            Nächstes
                        </Button>
                    </Stack>
                )}
            </Stack>
            <Grid container spacing={{ xs: 2, md: 3 }}>
                <Grid item xs={12} md={8}>
                    <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>
                        <Box sx={{ position: 'relative', width: '100%', paddingTop: '56.25%', borderRadius: 2, overflow: 'hidden', bgcolor: 'grey.100' }}>
                            {ytId ? (
                                <iframe
                                    title={item?.title || 'video'}
                                    src={`https://www.youtube.com/embed/${ytId}?rel=0`}
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    allowFullScreen
                                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
                                />
                            ) : (
                                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
                                    <Typography variant="body2">{t('video.playerPlaceholder', 'Video Player Placeholder')}</Typography>
                                </Box>
                            )}
                        </Box>
                        <Typography variant="h5" sx={{ mt: 2 }}>
                            {item?.title || t('video.titlePlaceholder', 'Videotitel')}
                        </Typography>
                        {itemTags.length > 0 && (
                            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}>
                                {itemTags.map(tag => <Chip key={tag} label={tag} size="small" />)}
                            </Stack>
                        )}
                        {item?.description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                {item.description}
                            </Typography>
                        )}
                    </Paper>

                    <Box sx={{ my: 3 }}>
                        <AdSlot slotName="detail-mid" height={250} />
                    </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Typography variant="h6" sx={{ mb: 2 }}>{t('video.related', 'Ähnliche Videos')}</Typography>
                    <Stack spacing={2}>
                        {related.map(v => (
                            <VideoCard key={v.id} video={v} onClick={(id) => navigate(`/video/${id}`)} />
                        ))}
                    </Stack>
                    <Box sx={{ mt: 3 }}>
                        <AdSlot slotName="detail-side" height={600} />
                    </Box>
                </Grid>
            </Grid>
        </Box>
    )
}

export default VideoDetail